import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': { backgroundColor: theme.palette.error.dark },
  },
  warningText: { color: theme.palette.error.main, fontWeight: 600 },
  boundWarning: {
    backgroundColor: theme.palette.warning.light ?? '#fff3e0',
    border: `1px solid ${theme.palette.warning.main ?? '#ff9800'}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));

type DeleteStatus = 'idle' | 'queuing' | 'waiting' | 'removing' | 'done' | 'error';

export function AzureDeleteResourceCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const entityRef = `resource:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const meta = entity.metadata as Record<string, any>;
  const resourceName = entity.metadata.name;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const pipelineName = meta.adoPipelineName as string | undefined;
  const repoName = meta.adoRepoName as string | undefined;
  const resourceType = meta.resourceType as string | undefined;
  // Support new boundComponents (comma-separated) and legacy boundComponent
  const boundComponentsRaw = (meta.boundComponents as string) || (meta.boundComponent as string) || '';
  const isBound = boundComponentsRaw.trim() !== '';

  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState<DeleteStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [pollStatus, setPollStatus] = useState('');
  const [runUrl, setRunUrl] = useState('');

  // Clear state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmText('');
      setStatus('idle');
      setErrorMsg('');
      setPollStatus('');
      setRunUrl('');
    }
  }, [open]);

  const waitForRun = async (
    baseUrl: string,
    token: string,
    runId: number,
  ): Promise<'succeeded' | 'failed'> => {
    for (;;) {
      await new Promise(r => setTimeout(r, 10_000));
      try {
        const res = await fetch(
          `${baseUrl}/pipelines/run-status?organization=${encodeURIComponent(organization!)}&project=${encodeURIComponent(project!)}&runId=${runId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) continue;
        const data = await res.json();
        setPollStatus(`${data.status}${data.result ? ` (${data.result})` : ''}`);
        if (data.status === 'completed') {
          return data.result === 'succeeded' ? 'succeeded' : 'failed';
        }
      } catch {
        // transient
      }
    }
  };

  const handleDelete = async () => {
    setStatus('queuing');
    setErrorMsg('');
    setPollStatus('');

    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token = '' } = await identityApi.getCredentials();

      if (pipelineName && organization && project) {
        // 1. Queue destroy pipeline
        const destroyRes = await fetch(`${baseUrl}/entities/destroy`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef,
            organization,
            project,
            pipelineName,
            repoName,
            resourceType,
          }),
        });
        if (!destroyRes.ok) {
          const err = await destroyRes.json().catch(() => ({ error: destroyRes.statusText })) as any;
          throw new Error(err.error ?? `HTTP ${destroyRes.status}`);
        }
        const { runId, runUrl: url } = await destroyRes.json();
        setRunUrl(url);
        setStatus('waiting');

        // 2. Poll until complete
        const result = await waitForRun(baseUrl, token, runId);
        if (result === 'failed') {
          throw new Error(
            'Destroy pipeline failed. The resource has NOT been removed from the catalog. Check the pipeline run for details.',
          );
        }
      }

      // 3. Remove from catalog
      setStatus('removing');
      const locationAnnotation = entity.metadata.annotations?.['backstage.io/managed-by-location'];
      if (locationAnnotation) {
        try {
          const location = await catalogApi.getLocationByRef(locationAnnotation);
          if (location) await catalogApi.removeLocationById(location.id);
        } catch {
          // best-effort
        }
      }
      await catalogApi.removeEntityByUid(entity.metadata.uid!);

      // 4. Best-effort: delete ADO repo + pipeline definitions
      if (organization && project && repoName) {
        try {
          await fetch(
            `${baseUrl}/ado/repo?entityRef=${encodeURIComponent(entityRef)}&organization=${encodeURIComponent(organization)}&project=${encodeURIComponent(project)}&repoName=${encodeURIComponent(repoName)}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // best-effort
        }
      }

      setStatus('done');
      setTimeout(() => navigate('/catalog'), 3000);
    } catch (e: any) {
      setStatus('error');
      setErrorMsg(e.message ?? 'Unknown error');
    }
  };

  const statusLabel: Record<DeleteStatus, string> = {
    idle: '',
    queuing: 'Queuing destroy pipeline…',
    waiting: 'Waiting for destroy pipeline to complete…',
    removing: 'Removing catalog entry…',
    done: 'Done! Redirecting to catalog…',
    error: '',
  };

  const isInProgress = status === 'queuing' || status === 'waiting' || status === 'removing';

  return (
    <>
      <InfoCard title="Management" noPadding={false}>
        {isBound && (
          <Box className={classes.boundWarning}>
            <Typography variant="body2">
              <strong>This resource is currently bound to an application.</strong> Unbind it from the
              application's Management tab before deleting.
            </Typography>
          </Box>
        )}
        <Typography variant="body2" gutterBottom>
          Permanently destroy this resource and remove it from the catalog.
        </Typography>
        <Box mt={2}>
          <Button
            variant="contained"
            className={classes.dangerButton}
            startIcon={<DeleteForeverIcon />}
            onClick={() => setOpen(true)}
            disabled={isBound || isInProgress || status === 'done'}
          >
            Delete Resource
          </Button>
        </Box>
      </InfoCard>

      <Dialog
        open={open}
        onClose={() => { if (!isInProgress) setOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Resource: {resourceName}</DialogTitle>
        <DialogContent>
          {status === 'idle' && (
            <>
              <DialogContentText>This will:</DialogContentText>
              <ul>
                {pipelineName && (
                  <li>Queue the <strong>destroy</strong> pipeline to tear down the Azure resource</li>
                )}
                <li>Wait for the destroy pipeline to complete successfully</li>
                <li>Remove this resource from the Backstage catalog</li>
                {repoName && (
                  <li>Delete the ADO repository and pipeline definitions (best-effort)</li>
                )}
              </ul>
              <DialogContentText className={classes.warningText}>
                This action is irreversible. Type <strong>{resourceName}</strong> to confirm.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={resourceName}
                variant="outlined"
              />
            </>
          )}

          {isInProgress && (
            <>
              <Progress />
              <DialogContentText style={{ marginTop: 16 }}>
                {statusLabel[status]}
              </DialogContentText>
              {runUrl && (
                <DialogContentText>
                  <a href={runUrl} target="_blank" rel="noopener noreferrer">
                    View pipeline run →
                  </a>
                </DialogContentText>
              )}
              {pollStatus && (
                <DialogContentText>
                  <Typography variant="caption" color="textSecondary">{pollStatus}</Typography>
                </DialogContentText>
              )}
            </>
          )}

          {status === 'done' && (
            <DialogContentText>
              Resource destroyed and removed from catalog. Redirecting…
            </DialogContentText>
          )}

          {status === 'error' && (
            <DialogContentText color="error">{errorMsg}</DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            disabled={isInProgress}
          >
            {status === 'error' ? 'Close' : 'Cancel'}
          </Button>
          {status === 'idle' && (
            <Button
              className={classes.dangerButton}
              variant="contained"
              onClick={handleDelete}
              disabled={confirmText !== resourceName}
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
